import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { LocalSessionService } from 'src/services/local-session.service';
import { PaymayaPaymentService, ParkingRecord } from 'src/services/paymaya-payment.service';
import firebase from 'firebase';
import Swal from 'sweetalert2';
import { PayMayaService } from 'src/services/paymaya.service';
import { RouteHelperService } from 'src/services/route-helper.service';
import { environment } from 'src/environments/environment';

interface PlateSearchResult {
  plate_number: string;
  date: string;
  status: 'paid' | 'unpaid';
  amount?: number;
  calculatedAmount?: number;
  // Add original record data for payment processing
  originalRecord?: any;
}

@Component({
  selector: 'app-mystreetparking',
  templateUrl: './mystreetparking.component.html',
  styleUrls: ['./mystreetparking.component.css']
})
export class MyStreetParkingComponent implements OnInit {

  plateNumber: string = '';
  searchedPlateNumber: string = ''; // New property to store the searched plate number
  searchResults: PlateSearchResult[] = [];
  isLoading: boolean = false;
  hasSearched: boolean = false;
  errorMessage: string = '';
  showResults: boolean = false; // New property to control view state

  private apiUrl = `${environment.apiBaseUrl}/searchPlateNumber`;

  constructor(
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private localSessionService: LocalSessionService,
    private paymayaPaymentService: PaymayaPaymentService,
    private payMayaService: PayMayaService,
    public routeHelper: RouteHelperService
  ) { }

  ngOnInit(): void {
    // Handle payment status returns
    this.route.queryParams.subscribe(params => {
      if (params['payment']) {
        this.handlePaymentStatusReturn(params['payment']);
      }
    });
  }

  async searchPlate() {
    if (!this.plateNumber.trim()) {
      this.errorMessage = 'Please enter a plate number';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.hasSearched = true;
    this.searchedPlateNumber = this.plateNumber.trim().toUpperCase(); // Update the new property
    this.showResults = true; // Show results view

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-source': 'street-parking'
    });

    const body = {
      plate_number: this.searchedPlateNumber,
      source: 'street-parking',
      limit: 10 // Increase limit to get more unpaid records
    };

    try {
      const response: any = await this.http.post(this.apiUrl, body, { headers }).toPromise();
      
      if (response) {
        if (response.success === false) {
          // Vehicle not found in system
          this.errorMessage = response.message || 'Plate number not found in the system.';
          this.searchResults = [];
        } else if (response.success && response.logs && Array.isArray(response.logs)) {
          // Found logs - filter and process ONLY unpaid records for street parking search
          const unpaidLogs = response.logs.filter(item => !item.paid);
          
          if (unpaidLogs.length === 0) {
            // No unpaid records found
            this.searchResults = [];
          } else {
            // Process unpaid records and calculate amounts
            const processedResults = await Promise.all(unpaidLogs.map(async (item: any) => {
              const result: PlateSearchResult = {
                plate_number: item.plate_number || this.searchedPlateNumber,
                date: this.formatDate(item.time_in || item.date),
                status: 'unpaid', // Always unpaid for street parking search
                originalRecord: item
              };
              
              // Calculate amount for unpaid sessions
              if (this.paymayaPaymentService.shouldShowPayNowButton(item)) {
                result.calculatedAmount = await this.paymayaPaymentService.calculateParkingFee(item);
                result.amount = result.calculatedAmount;
              }
              
              return result;
            }));

            // Filter out records with amount 0, null, or undefined
            this.searchResults = processedResults.filter((result: PlateSearchResult) => 
              result.amount && result.amount > 0
            ) as PlateSearchResult[];
          }
        } else if (response.logs && response.logs.length === 0) {
          // No logs found but vehicle exists
          this.searchResults = [];
        } else {
          this.searchResults = [];
        }
      } else {
        this.searchResults = [];
      }
    } catch (error) {
      console.error('Search error:', error);
      if (error.status === 404) {
        this.errorMessage = 'Plate number not found in the system.';
      } else {
        this.errorMessage = 'Unable to search at the moment. Please try again later.';
      }
      this.searchResults = [];
    } finally {
      this.isLoading = false;
    }
  }

  private formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  }

  cleanPlateNumber(event: any) {
    // Remove all special characters, spaces, and keep only letters and numbers
    const cleaned = event.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    this.plateNumber = cleaned;
  }

  clearSearch() {
    this.plateNumber = '';
    this.searchedPlateNumber = ''; // Clear the new property
    this.searchResults = [];
    this.hasSearched = false;
    this.errorMessage = '';
    this.showResults = false; // Hide results view
  }

  goBackToSearch() {
    this.showResults = false; // Hide results view and show search form
  }

  onResultCardClick() {
    // Check if user is authenticated
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const currentSession = this.localSessionService.getCurrentSession();
    
    if (user && user.uid && currentSession && currentSession.uid === user.uid) {
      // User is authenticated, redirect to user dashboard
      this.router.navigate([this.routeHelper.getRoutes().dashboard]);
    } else {
      // User is not authenticated, redirect to auth page
      this.router.navigate([this.routeHelper.getRoutes().auth]);
    }
  }

  async payNow(result: PlateSearchResult, event: Event) {
    // Prevent card click event
    event.stopPropagation();
    
    if (!result.originalRecord) {
      Swal.fire({
        title: 'Error',
        text: 'Unable to process payment. Please try searching again.',
        icon: 'error',
        confirmButtonColor: '#2196f3'
      });
      return;
    }

    try {
      // Create a guest user object for payment processing
      const guestUser = {
        uid: null, // Guest payment
        displayName: 'Guest User',
        phoneNumber: '',
        email: ''
      };

      // Use the payment service to open payment modal
      const paymentSuccess = await this.paymayaPaymentService.openSinglePaymentModal(
        result.originalRecord, 
        guestUser,
        this.routeHelper.getMyParkingRoot()
      );

    } catch (error) {
      console.error('Error initiating payment:', error);
      Swal.fire({
        title: 'Error',
        text: 'Unable to process payment at this time. Please try again later.',
        icon: 'error',
        confirmButtonColor: '#2196f3'
      });
    }
  }

  shouldShowPayButton(result: PlateSearchResult): boolean {
    return result.status === 'unpaid' && 
           result.originalRecord && 
           this.paymayaPaymentService.shouldShowPayNowButton(result.originalRecord);
  }

  getTotalAmountDue(): number {
    return this.searchResults
      .filter(result => result.amount && result.status === 'unpaid')
      .reduce((total, result) => total + (result.amount || 0), 0);
  }

  getUniqueZones(): string[] {
    const zones = this.searchResults
      .map(result => result.originalRecord?.zone_name)
      .filter(zone => zone);
    return [...new Set(zones)];
  }

  async payAll(event: Event) {
    // Prevent any event propagation
    event.stopPropagation();
    
    if (!this.searchResults || this.searchResults.length === 0) {
      Swal.fire({
        title: 'Error',
        text: 'No unpaid records found to pay.',
        icon: 'error',
        confirmButtonColor: '#2196f3'
      });
      return;
    }

    // Get all unpaid records that have payment capability
    const payableRecords = this.searchResults
      .filter(result => result.originalRecord && this.paymayaPaymentService.shouldShowPayNowButton(result.originalRecord))
      .map(result => result.originalRecord);

    if (payableRecords.length === 0) {
      Swal.fire({
        title: 'No Payable Records',
        text: 'No parking sessions are currently available for payment.',
        icon: 'info',
        confirmButtonColor: '#2196f3'
      });
      return;
    }

    try {
      // Calculate total amount for all unpaid sessions
      const totalAmount = await this.calculateTotalUnpaidAmount();
      
      // Generate HTML for all unpaid sessions
      const sessionsHtml = this.searchResults
        .filter(result => result.originalRecord && this.paymayaPaymentService.shouldShowPayNowButton(result.originalRecord))
        .map(result => `
          <div style="background: #f8f9fa; padding: 12px; border-radius: 6px; margin-bottom: 10px; border-left: 4px solid #e6e6e6;">
            <div style="display: flex; justify-content: between; align-items: start;">
              <div style="flex: 1;">
                <p style="margin: 0 0 8px 0; font-weight: 600; color: #333;"><strong>${result.plate_number}</strong></p>
                <div style="font-size: 13px; line-height: 1.4; color: #666;">
                  <p style="margin: 0;"><strong>Start:</strong> ${result.date}</p>
                  <p style="margin: 0;"><strong>End:</strong> ${this.getEffectiveEndTime(result.originalRecord)}</p>
                </div>
              </div>
              <div style="text-align: right;">
                <p style="margin: 0; font-size: 16px; font-weight: bold;">₱${result.calculatedAmount || result.amount || 0}</p>
              </div>
            </div>
          </div>
        `).join('');
      
      // Show SweetAlert2 modal with all unpaid sessions
      const result = await Swal.fire({
        title: 'Pay All Unpaid Parking Fees',
        html: `
          <div style="text-align: left; margin: 20px 0;">
            <div style="background: #fff3cd; padding: 15px; border-radius: 8px; margin-bottom: 15px; border: 1px solid #e6e6e6;">
              <div style="display: flex; align-items: center; font-size: 14px; color: #856404;">
                <i class="fas fa-exclamation-triangle" style="margin-right: 8px; color: #f39c12;"></i>
                <div>
                  <p style="margin: 0; font-weight: 600;">Paying All Unpaid Parking Fees</p>
                  <p style="margin: 0;">You are about to pay for ${payableRecords.length} unpaid parking session${payableRecords.length > 1 ? 's' : ''}.</p>
                </div>
              </div>
            </div>
            
            <div style="margin-bottom: 15px;">
              <h4 style="margin: 0 0 10px 0; color: #333; font-size: 16px;">Unpaid Parking Fees (${payableRecords.length})</h4>
              <div style="max-height: 300px; overflow-y: auto; border: 1px solid #e6e6e6; border-radius: 6px; padding: 10px;">
                ${sessionsHtml}
              </div>
            </div>
            
            <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; margin-bottom: 15px;">
              <p style="margin: 0 0 5px 0; color: black; font-size: 14px;">Total Amount to Pay</p>
              <p style="margin: 0; font-size: 2.5rem; font-weight: bold; color: black;">₱${totalAmount}</p>
            </div>
            
            <div class="text-center pt-8">
              <p class="text-blue-100 mt-2 mb-4">
                  <i class="fas fa-shield-alt mr-2"></i>Secure payment powered by
              </p>
              <img src="assets/img/Maya_logo.png" alt="Paymaya" style="width: 110px;" class="mt-2 mx-auto">
            </div>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Pay Now',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#4829AA',
        cancelButtonColor: '#2e2e2e',
        width: '750px',
        allowEscapeKey: true,
        allowOutsideClick: true,
        showLoaderOnConfirm: true,
        preConfirm: async () => {
          try {
            return await this.processPaymentForAllUnpaid(totalAmount, payableRecords);
          } catch (error) {
            Swal.showValidationMessage(
              `Payment failed: ${error.message || 'Unknown error'}`
            );
            return false;
          }
        }
      });

    } catch (error) {
      console.error('Error initiating bulk payment:', error);
      Swal.fire({
        title: 'Error',
        text: 'Unable to process bulk payment at this time. Please try again later.',
        icon: 'error',
        confirmButtonColor: '#2196f3'
      });
    }
  }

  private getEffectiveEndTime(record: any): string {
    // If there's auto_end_time and no time_out, use auto_end_time (format if it's an object)
    if (record.auto_end_time && !record.time_out) {
      return this.formatDate(record.auto_end_time);
    }
    // Otherwise use time_out or 'Active'
    return record.time_out ? this.formatDate(record.time_out) : 'Active';
  }

  private async calculateTotalUnpaidAmount(): Promise<number> {
    let total = 0;
    for (const result of this.searchResults) {
      if (result.originalRecord && this.paymayaPaymentService.shouldShowPayNowButton(result.originalRecord)) {
        if (result.calculatedAmount) {
          total += result.calculatedAmount;
        } else {
          const amount = await this.paymayaPaymentService.calculateParkingFee(result.originalRecord);
          total += amount;
        }
      }
    }
    return total;
  }

  private async processPaymentForAllUnpaid(totalAmount: number, payableRecords: any[]): Promise<boolean> {
    if (payableRecords.length === 0) {
      throw new Error('No unpaid records to process');
    }

    try {
      if (totalAmount <= 0) {
        throw new Error('Unable to calculate total parking fee. Please try again.');
      }

      // Create guest user object
      const guestUser = {
        uid: null, // Guest payment
        displayName: 'Guest User',
        phoneNumber: '',
        email: ''
      };

      // Create invoice/payment record for all unpaid sessions
      const invoiceData = {
        user_id: null, // Guest payment
        plate_numbers: payableRecords.map(record => record.plate_number || 'Unknown'),
        zone_names: payableRecords.map(record => record.zone_name || 'Unknown').join(', '),
        parking_session_ids: payableRecords.map(record => record.id || ''),
        amount: totalAmount,
        currency: 'PHP',
        status: 'PENDING',
        payment_status: 'PENDING',
        created_at: new Date(),
        payment_method: 'maya',
        session_count: payableRecords.length,
        payment_type: 'bulk_unpaid_sessions',
        sessions_detail: payableRecords.map(record => ({
          id: record.id || '',
          plate_number: record.plate_number || 'Unknown',
          zone_name: record.zone_name || 'Unknown',
          slot_name: record.slot_name || 'N/A',
          time_in: record.time_in || '',
          time_out: record.time_out || null,
          auto_end_time: record.auto_end_time || null,
          amount: record.amount_due || record.amount_paid || 0
        }))
      };

      // Generate Maya checkout payload for all sessions
      const checkoutPayload = this.generateMayaCheckoutPayloadForAll(invoiceData, guestUser);
      
      if (!checkoutPayload) {
        throw new Error('Unable to generate payment details. Please try again.');
      }

      // Create Maya checkout using paymayaPaymentService
      const checkoutResponse: any = await this.payMayaService.createCheckout(checkoutPayload).toPromise();
      
      // Handle both old and new PayMaya response structures
      const redirectUrl = checkoutResponse?.redirectUrl || checkoutResponse?.checkoutUrl;
      const checkoutId = checkoutResponse?.checkoutId;
      
      if (checkoutResponse && redirectUrl && checkoutId) {
        // Store payment record before redirect
        await this.storePaymentRecord({
          ...invoiceData,
          checkout_id: checkoutId,
          checkout_url: redirectUrl,
          maya_response: checkoutResponse,
          transaction_reference: this.generatePaymentReferenceForAll()
        });

        // Store payment attempt in localStorage for recovery
        localStorage.setItem('lastPaymentAttempt', JSON.stringify({
          checkout_id: checkoutId,
          parking_session_ids: payableRecords.map(record => record.id),
          plate_numbers: payableRecords.map(record => record.plate_number),
          zone_names: payableRecords.map(record => record.zone_name),
          amount: totalAmount,
          session_count: payableRecords.length,
          timestamp: new Date().toISOString(),
          user_id: null, // Guest payment
          transaction_reference: this.generatePaymentReferenceForAll()
        }));

        // Redirect to Maya checkout
        window.location.href = redirectUrl;
        return true; // Success for SweetAlert2
      } else {
        throw new Error('Invalid checkout response from Maya: ' + JSON.stringify(checkoutResponse));
      }

    } catch (error) {
      console.error('Payment processing error for all unpaid:', error);
      throw error; // Re-throw for SweetAlert2 to handle
    }
  }

  private handlePaymentStatusReturn(paymentStatus: string) {
    const statusMessages = {
      'success': 'Payment completed successfully! Your parking session has been paid.',
      'failed': 'Payment failed. Please try again or contact support.',
      'cancelled': 'Payment was cancelled. You can try again when ready.'
    };

    const statusIcons = {
      'success': 'success',
      'failed': 'error',
      'cancelled': 'warning'
    };

    const message = statusMessages[paymentStatus] || 'Payment status unknown.';
    const icon = statusIcons[paymentStatus] || 'info';

    Swal.fire({
      title: 'Payment Status',
      text: message,
      icon: icon as any,
      confirmButtonColor: '#2196f3',
      confirmButtonText: 'OK'
    }).then(() => {
      // Clear query params and refresh search if there was a plate number
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {},
        replaceUrl: true
      });
      
      // Refresh search results if user had searched
      if (this.hasSearched && this.searchedPlateNumber) {
        setTimeout(() => this.searchPlate(), 500);
      } else {
        // If no search was active, go back to search form
        this.showResults = false;
      }

      // Show registration prompt after successful payment
      if (paymentStatus === 'success') {
        setTimeout(() => {
          this.showRegistrationPrompt();
        }, 1000);
      }
    });
  }

  private showRegistrationPrompt() {
    // Check if user is authenticated
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const currentSession = this.localSessionService.getCurrentSession();
    
    const isAuthenticated = user && user.uid && currentSession && currentSession.uid === user.uid;
    
    if (!isAuthenticated) {
      Swal.fire({
        title: 'Want to track your vehicles?',
        html: `
          <div class="text-left">
            <p class="mb-4">Create an account to:</p>
            <ul class="text-left mb-4">
              <li>• View complete vehicle history</li>
              <li>• Track payment history</li>
              <li>• Register multiple vehicles</li>
              <li>• Get notifications for new parking sessions</li>
            </ul>
            <p class="text-sm text-gray-600">It only takes a minute to set up!</p>
          </div>
        `,
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Create Account',
        cancelButtonText: 'Maybe Later',
        confirmButtonColor: '#2196f3',
        cancelButtonColor: '#6c757d'
      }).then((result) => {
        if (result.isConfirmed) {
          this.router.navigate([this.routeHelper.getRoutes().auth]);
        }
      });
    } else {
      // User is authenticated, show dashboard option
      Swal.fire({
        title: 'View your complete history?',
        html: `
          <div class="text-left">
            <p class="mb-4">You can view your complete vehicle and payment history in your dashboard.</p>
            <p class="text-sm text-gray-600">Access all your parking records and manage your vehicles.</p>
          </div>
        `,
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Go to Dashboard',
        cancelButtonText: 'Stay Here',
        confirmButtonColor: '#2196f3',
        cancelButtonColor: '#6c757d'
      }).then((result) => {
        if (result.isConfirmed) {
          this.router.navigate([this.routeHelper.getRoutes().dashboard]);
        }
      });
    }
  }

  private generateMayaCheckoutPayloadForAll(invoiceData: any, guestUser: any): any {
    const userDisplayName = guestUser?.displayName || 'Guest User';
    const [firstName, ...lastNameParts] = userDisplayName.split(' ');
    const lastName = lastNameParts.join(' ') || 'User';

    return {
      totalAmount: {
        value: invoiceData.amount,
        currency: 'PHP'
      },
      buyer: {
        firstName: firstName || 'Guest',
        lastName: lastName || 'User',
        contact: {
          phone: guestUser?.phoneNumber || '',
          email: guestUser?.email || ''
        }
      },
      items: [
        {
          name: `Parking Fees - Multiple Sessions`,
          quantity: invoiceData.session_count,
          totalAmount: {
            value: invoiceData.amount,
            currency: 'PHP'
          },
          description: `Payment for ${invoiceData.session_count} unpaid parking sessions: ${invoiceData.plate_numbers.join(', ')}`
        }
      ],
      reference: this.generatePaymentReferenceForAll(),
      redirectUrl: {
        success: `${window.location.origin}${this.routeHelper.getMyParkingRoot()}?payment=success`,
        failure: `${window.location.origin}${this.routeHelper.getMyParkingRoot()}?payment=failed`, 
        cancel: `${window.location.origin}${this.routeHelper.getMyParkingRoot()}?payment=cancelled`
      },
      metadata: {
        user_id: guestUser.uid,
        parking_session_ids: invoiceData.parking_session_ids,
        plate_numbers: invoiceData.plate_numbers,
        zone_names: invoiceData.zone_names,
        session_count: invoiceData.session_count,
        payment_type: 'bulk_unpaid_sessions'
      }
    };
  }

  private generatePaymentReferenceForAll(): string {
    // Create a short, unique reference under 36 characters for bulk payment
    const timestamp = Date.now().toString();
    // Take last 8 digits of timestamp for uniqueness
    const shortTimestamp = timestamp.slice(-8);
    
    // Generate random alphanumeric string
    const randomId = Math.random().toString(36).substring(2, 10).toUpperCase();
    
    // Format: BULK-XXXXXXXX-YYYYYYYY (max 27 chars)
    const reference = `BULK-${randomId}-${shortTimestamp}`;
    
    // Ensure we never exceed 36 characters
    return reference.length > 36 ? reference.substring(0, 36) : reference;
  }

  private async storePaymentRecord(paymentData: any): Promise<void> {
    try {
      
      // Clean up the payment data to avoid undefined values and ensure Firestore compatibility
      const cleanedData = this.cleanPaymentDataForFirestore(paymentData);
      
      await firebase.firestore().collection('online_payments').add({
        ...cleanedData,
        payment_method: 'maya',
        created_at: firebase.firestore.FieldValue.serverTimestamp(),
        updated_at: firebase.firestore.FieldValue.serverTimestamp()
      });
    } catch (error) {
      console.error('Error storing payment record:', error);
      throw error;
    }
  }

  private cleanPaymentDataForFirestore(data: any): any {
    const cleaned: any = {};
    
    // Copy all defined values, converting complex objects to simple types
    Object.keys(data).forEach(key => {
      const value = data[key];
      
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          // Handle arrays - filter out undefined values
          cleaned[key] = value.filter(item => item !== undefined && item !== null);
        } else if (typeof value === 'object' && value.constructor === Object) {
          // Handle plain objects - recursively clean
          const cleanedObj = this.cleanPaymentDataForFirestore(value);
          if (Object.keys(cleanedObj).length > 0) {
            cleaned[key] = cleanedObj;
          }
        } else if (typeof value === 'object' && value._seconds) {
          // Handle Firebase timestamps - keep as is
          cleaned[key] = value;
        } else if (value instanceof Date) {
          // Convert Date objects to Firestore timestamp
          // Import Firebase timestamp dynamically if needed
          cleaned[key] = value; // Firestore will handle Date conversion
        } else {
          // Handle primitive types
          cleaned[key] = value;
        }
      }
    });
    
    return cleaned;
  }
}